import type { FC, MouseEvent, Ref } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import { DateTimeField, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import type { DateTime } from "luxon"

import DateTimePanel from "./DateTimePanel"
import { PANEL_LINE } from "./DateRangeEditor/constants"
import { getPanelPlacement } from "./helpers"
import { usePickerLocale } from "./hooks"

/** Tall enough for the calendar, the clock's header, the padding and the buttons. */
const POPOVER_HEIGHT = 480

interface DateTimeInputProps {
  value: DateTime | null
  onChange: (value: DateTime | null) => void
  inputRef?: Ref<HTMLInputElement>
  disableFuture?: boolean
  error?: boolean
  helperText?: string
}

/**
 * A typed date and time, with a button that opens the calendar and the clock. Picks there
 * reach the value on Apply, like the date range picker.
 */
const DateTimeInput: FC<DateTimeInputProps> = ({
  value,
  onChange,
  inputRef,
  disableFuture = false,
  error,
  helperText,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMediumOrSmallDevice = useMediaQuery(theme.breakpoints.down("md"))
  const locale = usePickerLocale()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [picked, setPicked] = useState<DateTime | null>(value)

  const hasChanged = !!picked && !(value && picked.equals(value))

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setPicked(value)
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)
  const handleApply = () => {
    if (hasChanged) onChange(picked)
    handleClose()
  }

  const panel = (
    <DateTimePanel value={picked} onChange={setPicked} disableFuture={disableFuture} />
  )
  const actions = (
    <>
      <Button
        color="inherit"
        variant={isMediumOrSmallDevice ? "outlined" : "text"}
        // Outlined on a phone, with the same line as the picker panels.
        sx={{ borderColor: PANEL_LINE }}
        onClick={handleClose}
      >
        {t("generic.CANCEL")}
      </Button>
      <Button variant="contained" onClick={handleApply} disabled={!hasChanged}>
        {t("generic.APPLY")}
      </Button>
    </>
  )
  const placement = getPanelPlacement(anchorEl, POPOVER_HEIGHT)

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
      <DateTimeField
        value={value}
        onChange={onChange}
        inputRef={inputRef}
        disableFuture={disableFuture}
        size="small"
        fullWidth
        helperText={helperText}
        // The field leaves `error` out of its own props; undefined keeps its validation.
        slotProps={{ textField: { error } }}
        sx={{ mb: 0 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                size="small"
                aria-label={t("dates.OPEN_CALENDAR")}
                onClick={handleOpen}
              >
                <CalendarMonthOutlinedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {isMediumOrSmallDevice ? (
        <Dialog fullWidth open={!!anchorEl} onClose={handleClose}>
          <DialogContent sx={{ p: 2 }}>{panel}</DialogContent>
          <DialogActions sx={{ p: 2, pt: 0, "& > .MuiButton-root": { flex: 1 } }}>
            {actions}
          </DialogActions>
        </Dialog>
      ) : (
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: placement.above ? "top" : "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: placement.above ? "bottom" : "top",
            horizontal: "right",
          }}
          sx={{ "& .MuiPopover-paper": theme.mixins.dialogSurface }}
        >
          <Stack
            spacing={2}
            sx={{
              px: 2.5,
              py: 3,
              maxHeight: placement.height,
              overflowY: "auto",
              // The panel hides its overflow, so it would shrink instead of scrolling.
              "& > *": { flexShrink: 0 },
            }}
          >
            {panel}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              {actions}
            </Stack>
          </Stack>
        </Popover>
      )}
    </LocalizationProvider>
  )
}

export default DateTimeInput
